import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ThemeProvider } from "./theme/ThemeProvider";
import { MobileFrame } from "./components/layout/MobileFrame";
import { PublicOnly, RequireAdmin, RequireAuth, RootRedirect } from "./components/layout/AuthGate";

import { Onboarding1 } from "./screens/Onboarding1";
import { Onboarding2 } from "./screens/Onboarding2";
import { Login } from "./screens/Login";
import { SignUp } from "./screens/SignUp";
import { MapHome } from "./screens/MapHome";
import { Category } from "./screens/Category";
import { NowFeed } from "./screens/NowFeed";
import { Venue } from "./screens/Venue";
import { EventDetail } from "./screens/EventDetail";
import { Search } from "./screens/Search";
import { Saved } from "./screens/Saved";
import { Profile } from "./screens/Profile";
import { Friends } from "./screens/Friends";
import { Eat } from "./screens/Eat";
import { ArticleDetail } from "./screens/ArticleDetail";
import { BeenThere } from "./screens/BeenThere";
import { AdminBootstrap } from "./screens/admin/AdminBootstrap";
import { AdminLayout } from "./screens/admin/AdminLayout";
import { AdminSources } from "./screens/admin/AdminSources";
import { AdminEvents } from "./screens/admin/AdminEvents";
import { AdminEventEdit } from "./screens/admin/AdminEventEdit";
import { AdminArticles } from "./screens/admin/AdminArticles";
import { AdminArticleEdit } from "./screens/admin/AdminArticleEdit";
import { AdminUsers } from "./screens/admin/AdminUsers";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <MobileFrame>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<RootRedirect />} />

                <Route path="/welcome" element={<PublicOnly><Onboarding1 /></PublicOnly>} />
                <Route path="/welcome/interests" element={<PublicOnly><Onboarding2 /></PublicOnly>} />
                <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                <Route path="/signup" element={<PublicOnly><SignUp /></PublicOnly>} />

                {/* Public browsing — anon users can explore. */}
                <Route path="/discover" element={<MapHome />} />
                <Route path="/discover/categories" element={<Category />} />
                <Route path="/now" element={<NowFeed />} />
                <Route path="/eat" element={<Eat />} />
                <Route path="/eat/:id" element={<ArticleDetail />} />
                {/* Public — anyone can tick boxes; state is localStorage-only. */}
                <Route path="/been-there" element={<BeenThere />} />
                <Route path="/venue/:id" element={<Venue />} />
                {/* Imported events: id (e.g. "iamsterdam:abc-123") is
                    URL-encoded on navigation so react-router decodes it back
                    intact in useParams. */}
                <Route path="/event/:id" element={<EventDetail />} />
                <Route path="/search" element={<Search />} />

                {/* Saved + Friends require auth (personal data). The You tab
                    is intentionally NOT gated — Profile renders its own
                    sign-in CTA for anon so the bottom-nav "You" never traps
                    anon visitors at a redirect. */}
                <Route path="/saved" element={<RequireAuth><Saved /></RequireAuth>} />
                <Route path="/you" element={<Profile />} />
                <Route path="/you/friends" element={<RequireAuth><Friends /></RequireAuth>} />

                {/* One-shot bootstrap path — outside the RequireAdmin
                    tree because by definition the user isn't admin yet.
                    Sits BEFORE the /admin block so the more-specific
                    route matches first. */}
                <Route path="/admin/bootstrap" element={<RequireAuth><AdminBootstrap /></RequireAuth>} />

                {/* Admin console — full /admin/* tree behind RequireAdmin. */}
                <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                  <Route index element={<Navigate to="/admin/sources" replace />} />
                  <Route path="sources" element={<AdminSources />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="events/new" element={<AdminEventEdit />} />
                  <Route path="events/:id/edit" element={<AdminEventEdit />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="articles/:id/edit" element={<AdminArticleEdit />} />
                  <Route path="users" element={<AdminUsers />} />
                </Route>

                <Route path="*" element={<Navigate to="/discover" replace />} />
              </Routes>
            </Suspense>
          </MobileFrame>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
